import { AppLocale, toAppLocale } from '@shared/lib/locale'

/** Откуда берётся ролик: YouTube по ID или файл на нашем сервере. */
export const VideoSourceKind = {
  youtube: 'youtube',
  file: 'file',
} as const
export type VideoSourceKind = (typeof VideoSourceKind)[keyof typeof VideoSourceKind]

/** Значения по языкам; `ru` обязателен, на остальных языках его заменяет русский. */
export type PerLang<T> = { ru: T } & Partial<Record<AppLocale, T>>

/** Файл ролика и кадр-постер рядом с ним. */
export interface InstructionFile {
  src: string
  poster: string
}

interface InstructionVideoBase {
  id: string
  /** Ключи неймспейса `participant`. */
  titleKey: string
  textKey: string
}

/** Ролик на YouTube. */
export interface YoutubeInstructionVideo extends InstructionVideoBase {
  kind: typeof VideoSourceKind.youtube
  /** YouTube ID по языкам. */
  videoIds: PerLang<string>
}

/** Ролик-файл из `public/videos/instructions/`. */
export interface FileInstructionVideo extends InstructionVideoBase {
  kind: typeof VideoSourceKind.file
  files: PerLang<InstructionFile>
}

/** Ролик-инструкция. Нет ролика на языке интерфейса — берётся русский. */
export type InstructionVideo = YoutubeInstructionVideo | FileInstructionVideo

const VIDEO_FILES_DIR = '/videos/instructions'

/**
 * Файлы темы `<topic>-<lang>.mp4` + постер `<topic>-<lang>.jpg`; `ru` обязателен,
 * остальные языки — только те, на которых ролик есть (на прочих берётся русский).
 */
function topicFiles(topic: string, langs: AppLocale[] = ['kz', 'en']): PerLang<InstructionFile> {
  const file = (lang: AppLocale): InstructionFile => ({
    src: `${VIDEO_FILES_DIR}/${topic}-${lang}.mp4`,
    poster: `${VIDEO_FILES_DIR}/${topic}-${lang}.jpg`,
  })
  const files: PerLang<InstructionFile> = { ru: file('ru') }
  for (const lang of langs) files[lang] = file(lang)
  return files
}

function fileVideo(
  id: string,
  topic: string,
  keys: { titleKey: string; textKey: string },
  langs?: AppLocale[]
): FileInstructionVideo {
  return { id, kind: VideoSourceKind.file, files: topicFiles(topic, langs), ...keys }
}

/** Новый ролик — одна запись здесь + два ключа в `participant.json` (+ файлы, если ролик-файл). */
export const INSTRUCTION_VIDEOS: InstructionVideo[] = [
  {
    id: 'registration',
    kind: VideoSourceKind.youtube,
    videoIds: { ru: 'VoxTHevRhHE' },
    titleKey: 'instructions.registration.title',
    textKey: 'instructions.registration.text',
  },
  fileVideo('olympiad_registration', 'olympiad-registration', {
    titleKey: 'instructions.olympiad_registration.title',
    textKey: 'instructions.olympiad_registration.text',
  }),
  // Исходники kz/en на Drive обрезаны — пока только русский, на остальных языках он же.
  fileVideo(
    'platform_features',
    'platform-features',
    {
      titleKey: 'instructions.platform_features.title',
      textKey: 'instructions.platform_features.text',
    },
    []
  ),
  fileVideo('platform_issues', 'platform-issues', {
    titleKey: 'instructions.platform_issues.title',
    textKey: 'instructions.platform_issues.text',
  }),
]

function pickByLang<T>(values: PerLang<T>, lang: string): T {
  return values[toAppLocale(lang)] ?? values.ru
}

/** ID ролика на языке интерфейса; если на нём ролика нет — русский. */
export function pickVideoId(videoIds: PerLang<string>, lang: string): string {
  return pickByLang(videoIds, lang)
}

/** Файл ролика на языке интерфейса; если на нём ролика нет — русский. */
export function pickVideoFile(files: PerLang<InstructionFile>, lang: string): InstructionFile {
  return pickByLang(files, lang)
}

/** Запись списка, ролик которой показывает карточка в сайдбаре. */
const SIDEBAR_VIDEO_ID = 'registration'

/** Ролик, который показывает карточка в сайдбаре (по `id`, а не по месту в списке). */
export const SIDEBAR_VIDEO: YoutubeInstructionVideo = (() => {
  const video = INSTRUCTION_VIDEOS.find((v) => v.id === SIDEBAR_VIDEO_ID)
  if (!video || video.kind !== VideoSourceKind.youtube) {
    throw new Error(`Нет ролика сайдбара: ${SIDEBAR_VIDEO_ID}`)
  }
  return video
})()
